import React, { Fragment, ReactNode, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { queryCache, QueryKey, useMutation } from "react-query";
import { AxiosError } from "axios";
import get from "lodash/get";

import {
  Button,
  Dialog,
  Loader,
  ApiErrorBanner,
  DraftFileUploader,
  TextInput,
  showNotification,
  FormControlMessage,
  SelectInput,
  DatePicker,
} from "fe-shared/src/components";
import { CheckIcon } from "fe-shared/src/components/Icons";

import { getMergedContent } from "fe-shared/src/helpers/validation";
import { getDateObject, getDateString } from "fe-shared/src/helpers/dates";
import useValidation from "fe-shared/src/hooks/useValidation";
import useUpdateEffect from "fe-shared/src/hooks/useUpdateEffect";
import useMaterialsByCode from "fe-shared/src/hooks/useMaterialsByCode";

import { ApiError, FileStatus } from "fe-shared/src/types/common";
import { BusinessRole } from "fe-shared/src/types/domain";

import useTransactionInventory from "../../hooks/useTransactionInventory";
import getMaterialGroups from "../../helpers/materialsConverter";

import {
  EditDispositionParams,
  Transaction,
  TransactionMaterial,
} from "../../types";

import {
  minDate,
  validationConfig,
  initialContent,
  errorMessages,
  MATERIAL_GROUPS_BY_END_MARKET_ROLE_CODE,
} from "./modules/validation";
import {
  getEndMarketRoleCodeOptions,
  getMaterialsInputConfig,
} from "./modules/config";
import * as api from "./modules/api";

import styles from "./EditDispositionDialog.module.scss";

export interface Props {
  isActive: boolean;
  transaction: Transaction;
  onClose: () => void;
  dialogTitle: ReactNode;
  activitiesQueryKey: QueryKey;
  transactionDetailsQueryKey: QueryKey;
}

const EditDispositionDialog = (props: Props) => {
  const {
    isActive,
    onClose,
    transaction,
    dialogTitle,
    activitiesQueryKey,
    transactionDetailsQueryKey,
  } = props;
  const { t } = useTranslation();
  const maxDateRef = useRef(new Date());
  const { transactionId, sourceWarehouse, materials } = transaction;
  const businessRoleCode = sourceWarehouse?.participant?.businessRoleCode!;
  const isHauler = businessRoleCode === BusinessRole.Hauler;
  const endMarketRoleCodeOptions = useMemo(
    () => getEndMarketRoleCodeOptions(t),
    [t]
  );
  const materialsByCode = useMaterialsByCode();
  const { data: warehouseMaterials, isFetching } = useTransactionInventory(
    transactionId,
    sourceWarehouse?.warehouseId!
  );
  const [editDisposition, mutationResult] = useMutation<
    unknown,
    AxiosError<ApiError>,
    EditDispositionParams
  >(api.editDisposition, {
    onSuccess: () => {
      queryCache.invalidateQueries(transactionDetailsQueryKey);
      queryCache.invalidateQueries(activitiesQueryKey);
      onClose();
      showNotification(
        t("actions.EditDisposition.toastMessage"),
        undefined,
        CheckIcon
      );
    },
  });

  const allMaterials = useMemo(() => {
    const aggregatedMaterials = [...materials, ...warehouseMaterials].reduce(
      (result, m) => {
        if (result[m.materialCode]) {
          result[m.materialCode].units.unitQuantity += m.units.unitQuantity;
        } else {
          result[m.materialCode] = { ...m, units: { ...m.units } };
        }
        return result;
      },
      {} as { [key: string]: TransactionMaterial }
    );

    return Object.values(aggregatedMaterials);
  }, [materials, warehouseMaterials]);

  const mergedContent = useMemo(() => {
    const materialQuantityByCode = materials.reduce((result, m) => {
      result[m.materialCode] = m.units.unitQuantity;
      return result;
    }, {} as { [key: string]: number });

    return getMergedContent(initialContent, {
      endMarketRoleCode:
        transaction.targetWarehouse?.participant.businessRoleCode,
      dispositionCompanyName:
        transaction.targetWarehouse?.participant.displayName,
      scaleTicketDate: transaction.scaleTickets[0]?.createdDate,
      scaleTicketNumber: transaction.scaleTickets[0]?.ticketNumber,
      dispositionInvoiceDate: transaction.dispositionInvoice?.date,
      dispositionInvoiceNumber: transaction.dispositionInvoice?.number,
      attachments: (transaction.attachments || []).map((item) => ({
        ...item,
        path: item.documentPath,
        id: item.documentPath,
        status: FileStatus.Loaded,
      })),
      materialGroups: getMaterialGroups(allMaterials)
        .map((group) => ({
          ...group,
          items: group.items.map((m) => {
            const transactionQuantity = materialQuantityByCode[m.materialCode] || 0;
            const availableQuantity = m.units.unitQuantity;
            
            // maxQuantity should equal availableQuantity from aggregated allMaterials
            // which includes materials from transaction + warehouse
            return {
              materialCode: m.materialCode,
              units: {
                unitQuantity: transactionQuantity,
                unit: group.unit,
              },
              maxQuantity: availableQuantity,
              availableQuantity,
            };
          }),
        }))
        .filter((g) => !!g.items.length),
    });
  }, [materials, allMaterials, transaction]);

  const {
    reset,
    errors,
    values,
    isValid,
    validateAll,
    updateValue,
    validateValue,
  } = useValidation(validationConfig, mergedContent, errorMessages);
  const { materialGroups, endMarketRoleCode } = values;
  const { totalMaterialsQuantity, activeMaterialGroups } = useMemo(() => {
    const activeGroups = materialGroups.filter((g) =>
      MATERIAL_GROUPS_BY_END_MARKET_ROLE_CODE[endMarketRoleCode].includes(
        g.materialCategoryName
      )
    );

    return {
      activeMaterialGroups: activeGroups,
      totalMaterialsQuantity: activeGroups.reduce((sum, g) => {
        g.items.forEach((m) => {
          sum += Number(m.units.unitQuantity) || 0;
        });
        return sum;
      }, 0),
    };
  }, [endMarketRoleCode, materialGroups]);

  useUpdateEffect(() => {
    reset(mergedContent);
  }, [mergedContent]);

  const isSubmitDisabled =
    !activeMaterialGroups.length ||
    !totalMaterialsQuantity ||
    !!Object.keys(errors).length;

  const onSubmit = async () => {
    validateAll();
    if (isValid) {
      await editDisposition({
        ...values,
        transactionId,
        businessRoleCode,
        materialGroups: activeMaterialGroups,
      });
    }
  };

  const actions = [
    <Button
      key="1"
      variant="filled"
      onClick={onSubmit}
      disabled={isSubmitDisabled}
      id="edit-disposition-dialog-save-btn"
    >
      {t("save")}
    </Button>,
    <Button
      key="2"
      onClick={onClose}
      variant="outlined"
      id="edit-disposition-dialog-cancel-btn"
    >
      {t("cancel")}
    </Button>,
  ];

  const updateNumberValue = (
    key: string,
    value: unknown,
    isValidated?: boolean
  ) => {
    if (value === "") {
      updateValue(key, "", isValidated);
      return;
    }
    
    // Remove commas and dots from the value (e.g., "1,234.56" -> "123456")
    const cleanedValue = String(value).replace(/[.,]/g, '');
    
    // Check if the cleaned value is a valid number
    const numericValue = Number(cleanedValue);
    
    // Only update if it's a valid number (not NaN)
    // eslint-disable-next-line no-restricted-globals
    if (!isNaN(numericValue)) {
      updateValue(key, numericValue, isValidated);
    }
    // If invalid, simply don't update (ignore the input)
  };

  const getFormProps = (valueKey: string) => ({
    errorText: errors[valueKey],
    isError: !!errors[valueKey],
    value: get(values, valueKey) as any,
    wrapperClassName: styles.formInput,
    data: values,
    errors,
  });

  const renderDateAndNumber = (dateKey: string, numberKey: string) => (
    <div className={styles.dateAndNumber}>
      <DatePicker
        isNoOffset
        id={dateKey}
        minDate={minDate}
        inputWrapperClassName={styles.date}
        title={t(`transactions.details.disposition.${dateKey}`)}
        placeholder={t(
          `transactions.details.disposition.placeholders.${dateKey}`
        )}
        startDate={getDateObject(values[dateKey] as string)}
        setStartDate={(v) => {
          updateValue(dateKey, getDateString(v), true);
        }}
        maxDate={maxDateRef.current}
        inputClassName={styles.pickupApprovalDatePicker}
        isError={!!errors[dateKey]}
        errorText={errors[dateKey]}
      />
      <TextInput
        id={numberKey}
        width={160}
        title={t(`transactions.details.disposition.${numberKey}`)}
        placeholder={t(
          `transactions.details.disposition.placeholders.${numberKey}`
        )}
        onBlur={() => validateValue(numberKey)}
        onChange={(e, value) => {
          updateValue(numberKey, value);
        }}
        {...getFormProps(numberKey)}
      />
    </div>
  );

  return (
    <Dialog
      modal
      width={584}
      title={dialogTitle}
      isActive={isActive}
      onClose={onClose}
      actions={actions}
    >
      {(mutationResult.isLoading || isFetching) && <Loader isBlocking />}
      {mutationResult.isError && (
        <ApiErrorBanner
          id="edit-disposition-error-banner"
          requestStatus={mutationResult}
        />
      )}
      <h3 className={styles.sectionTitle}>
        {t(
          `transactions.details.disposition.receiver${isHauler ? "Hauler" : ""}`
        )}
      </h3>
      {!isHauler && (
        <SelectInput
          disabled
          id="endMarketRoleCode"
          onBlur={() => validateValue("endMarketRoleCode")}
          onChange={(e, value) => {
            updateValue("endMarketRoleCode", value, true);
          }}
          title={t("transactions.details.disposition.endMarketRoleCode")}
          placeholder={t(
            "transactions.details.disposition.placeholders.endMarketRoleCode"
          )}
          options={endMarketRoleCodeOptions}
          {...getFormProps("endMarketRoleCode")}
        />
      )}
      <TextInput
        disabled
        width="100%"
        id="dispositionCompanyName"
        onBlur={() => validateValue("dispositionCompanyName")}
        onChange={(e, value) => {
          updateValue("dispositionCompanyName", value, false);
        }}
        placeholder={t(
          "transactions.details.disposition.placeholders.companyName"
        )}
        title={t("transactions.details.disposition.companyName")}
        {...getFormProps("dispositionCompanyName")}
      />
      {materialGroups.map((group, idx) =>
        activeMaterialGroups.some(
          (g) => g.materialCategoryName === group.materialCategoryName
        ) ? (
          <Fragment key={group.materialCategoryName}>
            <h3 className={styles.sectionTitle}>
              {`${t(
                `transactions.details.disposition.materials.${group.materialCategoryName}`
              )}, ${t(`units.${group.unit}`)}`}
            </h3>
            {getMaterialsInputConfig(
              `materialGroups[${idx}].items`,
              materialsByCode,
              group
            ).map(({ valueKey, componentProps }, index) => {
              const { title, ...otherProps } = componentProps;

              return (
                <div key={valueKey} className={styles.row}>
                  <label htmlFor={valueKey} className={styles.rowTitle}>
                    {title}
                  </label>
                  <TextInput
                    required
                    id={valueKey}
                    onBlur={() => validateValue(valueKey)}
                    onChange={(e, value) => {
                      updateNumberValue(valueKey, value, false);
                    }}
                    {...getFormProps(valueKey)}
                    {...otherProps}
                  />
                  <span className={styles.rowMaxQuantity}>
                    {`/${(group.items[index]?.availableQuantity ?? 0).toLocaleString()}`}
                  </span>
                </div>
              );
            })}
          </Fragment>
        ) : null
      )}
      {!activeMaterialGroups.length && (
        <div className={styles.materialsEmptyState}>
          {t(
            `transactions.details.disposition.emptyState.${MATERIAL_GROUPS_BY_END_MARKET_ROLE_CODE[endMarketRoleCode][0]}`
          )}
        </div>
      )}
      <h3 className={styles.sectionTitle}>
        {t("transactions.details.disposition.documents")}
      </h3>
      <div id="attachments">
        <FormControlMessage
          id="attachments"
          className={styles.error}
          errorText={errors.attachments}
          isError={
            !!errors.attachments &&
            errors.attachments !== `"attachments" is not allowed`
          }
        />
        <DraftFileUploader
          files={values.attachments}
          path="transactions/attachment"
          onChange={(files) => updateValue("attachments", files, true)}
        />
      </div>
      {renderDateAndNumber("scaleTicketDate", "scaleTicketNumber")}
      {renderDateAndNumber(
        "dispositionInvoiceDate",
        "dispositionInvoiceNumber"
      )}
    </Dialog>
  );
};

export default EditDispositionDialog;
